/**
 * PwaInlineAudioPlayer Component
 *
 * Compact audio player for voice message playback in the PWA.
 * Renders play/pause, seekable progress bar, and duration display.
 *
 * Validates: Requirements 8.1
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface PwaInlineAudioPlayerProps {
  src: string;
  durationSeconds?: number;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (typeof err === 'object' &&
      err != null &&
      'name' in err &&
      (err as { name: string }).name === 'AbortError')
  );
}

export function PwaInlineAudioPlayer({ src, durationSeconds }: PwaInlineAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  /** Evita que el poll regenere `src` (token firmado) y corte la reproducción. */
  const [activeSrc, setActiveSrc] = useState(src);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!src || src === activeSrc) return;
    // Solo refrescar URL si el clip no está en uso (poll con token nuevo).
    if (audio && (!audio.paused || audio.currentTime > 0)) return;
    setActiveSrc(src);
  }, [src, activeSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onError = () => {
      setHasError(true);
      setIsPlaying(false);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || hasError) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    void audio.play().catch((err: unknown) => {
      // pause()/cambio de src interrumpen play() con AbortError — no es fallo de carga
      if (isAbortError(err)) return;
      setHasError(true);
    });
  }, [hasError]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      audio.currentTime = ratio * duration;
      setCurrentTime(audio.currentTime);
    },
    [duration]
  );

  const progress = duration > 0 ? currentTime / duration : 0;
  const displayTime = isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration);

  if (hasError) {
    return (
      <div className="pwa-audio-player pwa-audio-player--error">
        <span className="pwa-audio-player__error-text">⚠️ No se pudo cargar el audio</span>
      </div>
    );
  }

  return (
    <div className="pwa-audio-player">
      <audio ref={audioRef} src={activeSrc} preload="auto" />

      <button
        type="button"
        className="pwa-audio-player__play-btn"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>

      <div
        className="pwa-audio-player__progress"
        onClick={handleSeek}
        role="slider"
        aria-label="Progreso del audio"
        aria-valuenow={Math.round(currentTime)}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        tabIndex={0}
      >
        <div
          className="pwa-audio-player__progress-fill"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <span className="pwa-audio-player__time">{displayTime}</span>
    </div>
  );
}
